import type { ReactNode } from 'react';
import type { UIMessagePart } from 'ai';
import { ConnectIntegrationCard } from './connect-integration-card';
import { EnableCapabilityCard } from './enable-capability-card';
import { StarterCronProposalCard } from './starter-cron-proposal-card';
import { QuickRepliesCard } from './quick-replies-card';
import { OnboardingRecapCard } from './onboarding-recap-card';
import { ContinueOfferCard } from './continue-offer-card';
import { DockableCardRecord, DockableQuickReplies } from './chat-card-dock';
import {
  SIGNAL_PART_TYPE,
  type ConnectIntegrationSignalData,
  type ContinueOfferSignalData,
  type EnableCapabilitySignalData,
  type OnboardingRecapSignalData,
  type QuickRepliesSignalData,
  type StarterCronProposalSignalData,
  type ToolSignal,
  type ToolSignalType,
} from './tool-signals';

/**
 * FRONT-847: registry mapping each `data-<signal-type>` SSE part type to the
 * card component that renders it. Keyed by the same string the chat proxy
 * emits the part under, via `SIGNAL_PART_TYPE`. `message-bubble.tsx` renders
 * every registered signal part inline in the assistant message.
 */
/** Chat-level context threaded into signal cards that need it (FRONT-847). */
interface SignalCardContext {
  /**
   * FRONT-2465: the host message is still being produced (live stream, or a
   * turn running on the server). A `continue-offer` renders nothing meanwhile:
   * the part is streamed before the reply has finished, and a tap then would
   * start a second turn on top of the running one.
   */
  isStreaming?: boolean;
  /** Threaded from the chat hook's sendMessage; fires a follow-up turn. */
  onSendMessage?: (text: string) => void | Promise<void>;
  /** Text of the user message the current assistant turn is replying to. */
  originalUserText?: string;
  /**
   * 2026-07-21 (FRONT-907): starts a live voice call on this chat's own
   * session — used by a `quick-replies` option with `action: "call"`.
   */
  onStartCall?: () => void;
  /**
   * True when the host message is the last one in the conversation — a
   * `quick-replies` card only renders on the last message (FRONT-907: any
   * later turn means the offer was answered or overtaken, and the card
   * shouldn't resurface on reload).
   */
  isLastMessage?: boolean;
}

/**
 * Local helper: `SIGNAL_PART_TYPE` typed to its `data-<type>` literal so the
 * registry keys below are recognised as specific property names, not a
 * widened `string`. Same runtime as `SIGNAL_PART_TYPE`; the shared
 * `tool-signals.ts` helper stays byte-identical with its backend copy.
 */
const partType = <T extends ToolSignalType>(type: T): `data-${T}` =>
  SIGNAL_PART_TYPE(type) as `data-${T}`;

/** Renderer for one signal type, receiving exactly that type's payload. */
type SignalCardRenderer<T extends ToolSignalType> = (
  data: Extract<ToolSignal, { type: T }>['data'],
  key: string,
  ctx: SignalCardContext,
) => ReactNode;

/**
 * Keyed by the `data-<type>` part string, each entry's `data` payload is tied
 * to the matching `ToolSignal` union member — so a payload-shape drift (or a
 * card wired to the wrong signal) is caught by tsc, not at runtime.
 */
const SIGNAL_CARD_REGISTRY: {
  [T in ToolSignalType as `data-${T}`]: SignalCardRenderer<T>;
} = {
  [partType('connect-integration')]: (
    data: ConnectIntegrationSignalData,
    key: string,
    ctx: SignalCardContext,
  ) => (
    <ConnectIntegrationCard
      key={key}
      providerKey={data.providerKey}
      providerName={data.providerName}
      capability={data.capability}
      flavour={data.flavour}
      retryable={data.retryable}
      originalUserText={ctx.originalUserText}
      onSendMessage={ctx.onSendMessage}
    />
  ),
  [partType('enable-capability')]: (
    data: EnableCapabilitySignalData,
    key: string,
  ) => <EnableCapabilityCard key={key} agentId={data.agentId} capabilities={data.capabilities} />,
  [partType('starter-cron-proposal')]: (
    data: StarterCronProposalSignalData,
    key: string,
  ) => (
    // FRONT-3223: one collapsed line where the reply box decides it.
    <DockableCardRecord key={key} label="Starter automations">
      <StarterCronProposalCard crons={data.crons} />
    </DockableCardRecord>
  ),
  [partType('quick-replies')]: (
    data: QuickRepliesSignalData,
    key: string,
    ctx: SignalCardContext,
  ) => (
    // FRONT-3223: in the reply box instead, where the box decides cards.
    <DockableQuickReplies key={key}>
      <QuickRepliesCard
        options={data.options}
        onSendMessage={ctx.onSendMessage}
        onStartCall={ctx.onStartCall}
        isLastMessage={ctx.isLastMessage}
      />
    </DockableQuickReplies>
  ),
  [partType('onboarding-recap')]: (
    data: OnboardingRecapSignalData,
    key: string,
  ) => (
    <DockableCardRecord key={key} label="What EDDY remembers">
      <OnboardingRecapCard runId={data.runId} items={data.items} />
    </DockableCardRecord>
  ),
  [partType('continue-offer')]: (
    data: ContinueOfferSignalData,
    key: string,
    ctx: SignalCardContext,
  ) => (
    <ContinueOfferCard
      key={key}
      replacesText={data?.replacesText === true}
      prompt={typeof data?.prompt === 'string' ? data.prompt : undefined}
      onSendMessage={ctx.onSendMessage}
      isLastMessage={ctx.isLastMessage}
      isStreaming={ctx.isStreaming}
    />
  ),
};

type SignalDataPart = UIMessagePart<any, any> & { type: string; data?: { id?: string | number } };

function filterSignalParts(parts: UIMessagePart<any, any>[]): SignalDataPart[] {
  return (parts as SignalDataPart[]).filter(
    (p) => typeof p.type === 'string' && p.type in SIGNAL_CARD_REGISTRY,
  );
}

/**
 * Whether a message's `parts` contain any registered signal — used by
 * `message-bubble.tsx` so a message consisting only of a signal (no text, no
 * tool calls) still renders instead of being treated as empty.
 */
export function hasSignalParts(parts: UIMessagePart<any, any>[]): boolean {
  return filterSignalParts(parts).length > 0;
}

/**
 * Renders every registered signal part found in a message's `parts`, in
 * order, keyed by the part's `data.id`. Unregistered `data-*` part types are
 * ignored here (they're handled elsewhere, e.g. Truth Guard/stall banners).
 */
export function SignalCards({
  parts,
  onSendMessage,
  originalUserText,
  onStartCall,
  isLastMessage,
  isStreaming,
}: {
  parts: UIMessagePart<any, any>[];
} & SignalCardContext) {
  const signalParts = filterSignalParts(parts);

  if (signalParts.length === 0) return null;

  return (
    <>
      {signalParts.map((part, i) => {
        // Dynamic dispatch boundary: `part.type` is a runtime string and
        // `part.data` an untyped SSE payload, so the lookup + call is cast to a
        // generic renderer here. The registry DEFINITION above stays strictly
        // typed per signal, which is where payload drift is actually caught.
        const render = (
          SIGNAL_CARD_REGISTRY as Record<
            string,
            (data: unknown, key: string, ctx: SignalCardContext) => ReactNode
          >
        )[part.type];
        const key = String(part.data?.id ?? `${part.type}-${i}`);
        return render(part.data, key, {
          onSendMessage,
          originalUserText,
          onStartCall,
          isLastMessage,
          isStreaming,
        });
      })}
    </>
  );
}
